import { Form, useForm } from '@/components/forms';
import Input from '@/components/forms/Input';
import Textarea from '@/components/forms/Textarea';
import { useAttributes } from '@/hooks/useAttributes';
import { useDynamicLocale } from '@/hoslog-intl';
import { capitalizeFirst } from '@/lib/capitalizeFirstLetter';
import { useMemo } from 'react';
import { z } from 'zod';
import { useChargeMutation } from './hooks/useChargeMutation';
import { useChargeDetail } from './hooks/useChargesList';
import { TabType } from './table/helper';
import { getDefaultChargeType } from './utils';

const manageCommissionFormSchema = z.object({
  chargeType: z.string().optional(),
  categoryName: z.string().min(1, { message: 'common.form.validation.required' }),
  description: z.string().optional(),
});

const ManageChargeCategoryForm = ({
  chargeCategoryId,
  activeTab,
}: {
  chargeCategoryId?: string;
  activeTab?: TabType;
}) => {
  const { data: chargeType = [] } = useAttributes('chargeUserType') || {};

  const defaultChargeType = getDefaultChargeType(activeTab, chargeType);

  const { chargeDetail, isLoading } = useChargeDetail({
    chargeId: chargeCategoryId || '',
    agentType: defaultChargeType || '',
  });
  const initialValues = useMemo(() => {
    if (!chargeDetail) {
      return {
        chargeType: defaultChargeType || '',
        categoryName: '',
        description: '',
      };
    }
    return {
      chargeType: capitalizeFirst(activeTab as string) || '',
      categoryName: chargeDetail.categoryName || '',
      description: chargeDetail.description || '',
    };
  }, [chargeDetail]);

  const form = useForm({
    schema: manageCommissionFormSchema,
    mode: 'onBlur',
    values: initialValues,
  });
  const { t } = useDynamicLocale();
  const { mutateAsync } = useChargeMutation(activeTab || 'affiliate');

  return (
    <Form
      form={form}
      onSubmit={async (data) => {
        await mutateAsync({
          chargeCategoryId: chargeCategoryId ?? '',
          agentType: defaultChargeType || '',
          categoryName: data.categoryName,
          description: data.description || '',
        });
      }}
    >
      <fieldset disabled={isLoading}>
        <div className="w-full flex flex-col gap-4.5 p-4]">
          <Input
            disabled
            {...form.register('chargeType')}
            label="configurationManagement.charges.manageChargeModal.chargeType"
          />

          <Input
            label="configurationManagement.charges.manageChargeModal.categoryName"
            {...form.register('categoryName')}
            placeholder={t('configurationManagement.charges.manageChargeSlabModal.placeholder.enterCategoryName')}
          />

          <Textarea
            label="configurationManagement.charges.manageChargeSlabModal.description"
            {...form.register('description')}
            placeholder={t('configurationManagement.charges.manageChargeSlabModal.placeholder.writeSomething')}
            className="resize-none"
          />
        </div>
      </fieldset>
    </Form>
  );
};

export default ManageChargeCategoryForm;
